import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import SafeIcon from '../common/SafeIcon';
import Logo from './Logo';

const { FiTrello, FiTrendingUp, FiCheckCircle, FiClock, FiAlertCircle, FiArrowLeft, FiActivity, FiFolder, FiBarChart2 } = FiIcons;

const GlobalDashboard = ({ boards, onBoardSwitch }) => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('7d');

  // Calculate global metrics
  const calculateGlobalMetrics = () => {
    const boardList = Object.values(boards);
    
    let totalTasks = 0;
    let totalCompleted = 0;
    let totalInProgress = 0;
    let totalTodo = 0;
    let totalBacklog = 0;
    let totalOverdue = 0;
    let totalHighPriority = 0;

    // Board-specific metrics
    const boardMetrics = boardList.map(board => {
      const boardTotalTasks = Object.values(board.columns).reduce((sum, col) => sum + col.tasks.length, 0);
      const boardCompleted = board.columns.done?.tasks.length || 0;
      const boardInProgress = board.columns['in-progress']?.tasks.length || 0;
      const boardTodo = board.columns.todo?.tasks.length || 0;
      const boardBacklog = board.columns.backlog?.tasks.length || 0;

      const boardCompletionRate = boardTotalTasks > 0 ? Math.round((boardCompleted / boardTotalTasks) * 100) : 0;

      // Calculate board-specific overdue tasks
      const now = new Date();
      const boardOverdue = Object.values(board.columns).reduce((sum, col) => {
        return sum + col.tasks.filter(task => {
          if (!task.dueDate) return false;
          return new Date(task.dueDate) < now;
        }).length;
      }, 0);

      // Calculate board-specific high priority tasks
      const boardHighPriority = Object.values(board.columns).reduce((sum, col) => {
        return sum + col.tasks.filter(task => task.priority === 'high').length;
      }, 0);

      // Accumulate totals
      totalTasks += boardTotalTasks;
      totalCompleted += boardCompleted;
      totalInProgress += boardInProgress;
      totalTodo += boardTodo;
      totalBacklog += boardBacklog;
      totalOverdue += boardOverdue;
      totalHighPriority += boardHighPriority;

      return {
        id: board.id,
        name: board.name,
        totalTasks: boardTotalTasks,
        completed: boardCompleted,
        inProgress: boardInProgress,
        todo: boardTodo,
        backlog: boardBacklog,
        completionRate: boardCompletionRate,
        overdue: boardOverdue,
        highPriority: boardHighPriority
      };
    });

    const globalCompletionRate = totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;

    return {
      totalBoards: boardList.length,
      totalTasks,
      totalCompleted,
      totalInProgress,
      totalTodo,
      totalBacklog,
      globalCompletionRate,
      totalOverdue,
      totalHighPriority,
      boardMetrics
    };
  };

  // Prepare chart data
  const prepareChartData = () => {
    const metrics = calculateGlobalMetrics();
    
    // Column distribution data
    const columnData = [
      { name: 'Backlog', value: metrics.totalBacklog, color: '#6b7280' },
      { name: 'To Do', value: metrics.totalTodo, color: '#ef4444' },
      { name: 'In Progress', value: metrics.totalInProgress, color: '#eab308' },
      { name: 'Done', value: metrics.totalCompleted, color: '#22c55e' }
    ].filter(item => item.value > 0);

    // Board performance data
    const boardPerformanceData = metrics.boardMetrics.map(board => ({
      name: board.name,
      tasks: board.totalTasks,
      completed: board.completed,
      completionRate: board.completionRate
    }));

    // Board comparison data
    const boardComparisonData = metrics.boardMetrics.map(board => ({
      name: board.name,
      backlog: board.backlog,
      todo: board.todo,
      inProgress: board.inProgress,
      done: board.completed
    }));

    // Priority distribution data (across all boards)
    const priorityData = [];
    Object.values(boards).forEach(board => {
      Object.values(board.columns).forEach(column => {
        column.tasks.forEach(task => {
          if (task.priority) {
            const existing = priorityData.find(p => p.priority === task.priority);
            if (existing) {
              existing.count++;
            } else {
              priorityData.push({ priority: task.priority, count: 1 });
            }
          }
        });
      });
    });

    return { columnData, boardPerformanceData, boardComparisonData, priorityData };
  };

  const metrics = calculateGlobalMetrics();
  const { columnData, boardPerformanceData, boardComparisonData, priorityData } = prepareChartData();

  const MetricCard = ({ title, value, icon, color, subtitle }) => (
    <motion.div
      className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <SafeIcon icon={icon} className="w-6 h-6 text-white" />
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
      <p className="text-sm text-gray-600 mt-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-2">{subtitle}</p>}
    </motion.div>
  );

  const handleBoardClick = (boardId) => {
    onBoardSwitch(boardId);
    navigate(`/`);
  };

  const handleBoardDashboardClick = (boardId) => {
    onBoardSwitch(boardId);
    navigate(`/dashboard/${boardId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <motion.header 
        className="bg-white shadow-sm border-b border-gray-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <motion.button
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                </motion.button>
              </Link>
              <div className="flex items-center space-x-3">
                <Logo size="md" className="rounded-lg" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Global Dashboard</h1>
                  <p className="text-sm text-gray-600">All boards metrics and analytics</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
              <Link to="/">
                <motion.button
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiTrello} className="w-4 h-4" />
                  <span>View Boards</span>
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="container mx-auto px-4 py-8">
        {/* Global Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <MetricCard
            title="Total Boards"
            value={metrics.totalBoards}
            icon={FiFolder}
            color="bg-purple-600"
          />
          <MetricCard
            title="Total Tasks"
            value={metrics.totalTasks}
            icon={FiTrello}
            color="bg-blue-600"
          />
          <MetricCard
            title="Completed"
            value={metrics.totalCompleted}
            icon={FiCheckCircle}
            color="bg-green-600"
          />
          <MetricCard
            title="In Progress"
            value={metrics.totalInProgress}
            icon={FiClock}
            color="bg-yellow-600"
          />
          <MetricCard
            title="Global Completion Rate"
            value={`${metrics.globalCompletionRate}%`}
            icon={FiTrendingUp}
            color="bg-purple-600"
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Global Task Distribution */}
          <motion.div
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Global Task Distribution</h3>
            {columnData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={columnData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {columnData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <p>No tasks to display</p>
              </div>
            )}
          </motion.div>

          {/* Board Performance Comparison */}
          <motion.div
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Board Performance</h3>
            {boardPerformanceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={boardPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="completionRate" fill="#8b5cf6" name="Completion %" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <p>No board data to display</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Board Comparison Chart */}
        <motion.div
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Board Comparison</h3>
          {boardComparisonData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={boardComparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="backlog" stackId="a" fill="#6b7280" name="Backlog" />
                <Bar dataKey="todo" stackId="a" fill="#ef4444" name="To Do" />
                <Bar dataKey="inProgress" stackId="a" fill="#eab308" name="In Progress" />
                <Bar dataKey="done" stackId="a" fill="#22c55e" name="Done" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <p>No board data to display</p>
            </div>
          )}
        </motion.div>

        {/* Board Details Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
          {metrics.boardMetrics.map((board, index) => (
            <motion.div
              key={board.id}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
              onClick={() => handleBoardClick(board.id)}
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">{board.name}</h4>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBoardDashboardClick(board.id);
                    }}
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <SafeIcon icon={FiBarChart2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{board.totalTasks}</p>
                  <p className="text-xs text-gray-600">Total Tasks</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{board.completionRate}%</p>
                  <p className="text-xs text-gray-600">Completion</p>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{board.inProgress}</p>
                  <p className="text-xs text-gray-600">In Progress</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{board.overdue}</p>
                  <p className="text-xs text-gray-600">Overdue</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Global Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Status Overview</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Backlog Tasks</span>
                <span className="font-semibold text-gray-900">{metrics.totalBacklog}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">High Priority Tasks</span>
                <span className="font-semibold text-red-600">{metrics.totalHighPriority}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Overdue Tasks</span>
                <span className="font-semibold text-orange-600">{metrics.totalOverdue}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.6 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Tasks Pending</span>
                <span className="font-semibold text-yellow-600">{metrics.totalTodo + metrics.totalBacklog}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Active Tasks</span>
                <span className="font-semibold text-blue-600">{metrics.totalInProgress}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Avg Tasks per Board</span>
                <span className="font-semibold text-purple-600">
                  {metrics.totalBoards > 0 ? Math.round(metrics.totalTasks / metrics.totalBoards) : 0}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.7 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Priority Distribution</h3>
            {priorityData.length > 0 ? (
              <div className="space-y-2">
                {priorityData.map((priority, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-600 capitalize">{priority.priority}</span>
                    <span className="font-semibold text-gray-900">{priority.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No priority data available</p>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default GlobalDashboard;